import { useMemo } from 'react';
import { useApi } from './useApi';
import type { Kid, CreateKidInput, UpdateKidInput } from '../types';

export function useKidApi() {
  const call = useApi();
  // useMemo against the stable `call` ref so callers can safely depend on
  // this object in useEffect/useCallback without re-firing every render.
  return useMemo(() => ({
    list: () => call<Kid[]>('/api/v1/kids'),
    get: (kidId: string) => call<Kid>(`/api/v1/kids/${encodeURIComponent(kidId)}`),
    create: (input: CreateKidInput) => call<Kid>('/api/v1/kids', { method: 'POST', body: input }),
    update: (kidId: string, patch: UpdateKidInput) =>
      call<Kid>(`/api/v1/kids/${encodeURIComponent(kidId)}`, { method: 'PATCH', body: patch }),
    remove: (kidId: string) =>
      call<{ ok: true }>(`/api/v1/kids/${encodeURIComponent(kidId)}`, { method: 'DELETE' }),

    // ─── Profile photo (one per kid) ─────────────────────────────────────
    presignPhoto: (kidId: string, contentType: string, size: number) =>
      call<{ uploadUrl: string; fields: Record<string, string>; key: string; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/photo/presign`,
        { method: 'POST', body: { contentType, size } }
      ),
    photoUrl: (kidId: string) =>
      call<{ url: string; expiresAt: string }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/photo-url`
      ),
    removePhoto: (kidId: string) =>
      call<{ ok: true }>(
        `/api/v1/kids/${encodeURIComponent(kidId)}/photo`,
        { method: 'DELETE' }
      ),
    // ─── H1.5 — Year-end season recap (Reports tier, generated 1 Aug) ──
    seasonRecap: (kidId: string, season: string) =>
      call<{
        kidId: string;
        kidName: string;
        season: string;
        text: string;
        generatedAt: string;
        modelId: string;
      }>(`/api/v1/kids/${encodeURIComponent(kidId)}/season-recap/${encodeURIComponent(season)}`),
  }), [call]);
}
